/**
 * First-time visitor callout pointing at the shortcuts FAB. Renders a
 * small speech-bubble next to the FAB anchor with a bobbing animation
 * to draw attention.
 *
 * Lifecycle is owned by the parent (`EmbedViewer`) via the `visible` +
 * `onDismiss` props. The parent reads/writes the localStorage flag
 * (`nxorbit:shortcuts-hint-seen:<characterId>`) since the embed knows
 * the characterId; this component is just the visual.
 */
import { type ReactElement } from 'react';
import type { Shortcuts } from '@nxorbit/shared-schema';

interface Props {
  position: Shortcuts['position'];
  visible: boolean;
  onDismiss: () => void;
}

// Anchor the callout next to the FAB. The hint sits OUTSIDE the FAB
// (e.g., FAB at top-right → hint at top-right but shifted left), so
// the visual arrow on the hint points BACK toward the FAB.
const HINT_POSITION_CLASSES: Record<Shortcuts['position'], string> = {
  'top-left': 'top-4 left-16',
  'top-right': 'top-4 right-16',
  'bottom-left': 'bottom-4 left-16',
  'bottom-right': 'bottom-4 right-16',
};

export function ShortcutsHint({ position, visible, onDismiss }: Props): ReactElement | null {
  if (!visible) return null;
  const isRightCorner = position.endsWith('right');
  // Arrow direction:
  //  - FAB at right corner → hint sits to the LEFT of FAB → arrow points RIGHT (toward FAB)
  //  - FAB at left corner → hint sits to the RIGHT of FAB → arrow points LEFT (toward FAB)
  const text = isRightCorner ? '點這裡看常用問題 →' : '← 點這裡看常用問題';

  return (
    <div
      role="status"
      className={`pointer-events-auto absolute z-40 flex items-center gap-2 rounded-2xl border border-white/10 bg-slate-900/85 px-3.5 py-2 text-xs font-medium text-slate-100 shadow-2xl backdrop-blur-md nx-shortcuts-hint-bob ${
        HINT_POSITION_CLASSES[position]
      }`}
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.7)]" />
      <span className="whitespace-nowrap">{text}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="關閉提示"
        className="flex h-4 w-4 items-center justify-center rounded-full text-[11px] text-slate-400 transition hover:bg-white/10 hover:text-slate-100"
      >
        ×
      </button>
    </div>
  );
}

export default ShortcutsHint;
